/**
 * PMO 遅延 KPI の API 型。`apps/api/product/.../pmo-delay-schemas.ts` と対応する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO遅延KPI（引き直し/遅延のQ内累積トレンド・先Q実績ペース線・目標線・現時点差分）のAPI型定義
 * @graph-connects none
 */

/** 遅延評価の区分。`rework` = 引き直し、`delay` = 遅延 @graph-connects none */
export type PmoDelayCategory = 'rework' | 'delay';

/** 表示順（引き直し → 遅延）。API の series 順とも一致する @graph-connects none */
export const PMO_DELAY_CATEGORIES: readonly PmoDelayCategory[] = ['rework', 'delay'];

/** @graph-connects none */
export interface PmoDelayQuarter {
  fiscalYear: number;
  quarter: number;
  startDate: string;
  endDate: string;
  businessDays: number;
}

/** @graph-connects none */
export interface PmoDelayTrendPoint {
  date: string;
  isBusinessDay: boolean;
  businessDayIndex: number;
  /** as-of より後は null（線を引かない） */
  actualDays: number | null;
  previousQuarterDays: number;
  targetDays: number;
}

/** @graph-connects none */
export interface PmoDelaySummary {
  actualDays: number;
  targetDays: number;
  previousQuarterDays: number;
  /** 実績 − 目標。遅延日数は少ないほど良いので負が良い側 */
  diffDays: number;
  isWithinTarget: boolean;
  count: number;
}

/** @graph-connects none */
export interface PmoDelaySeries {
  category: PmoDelayCategory;
  previousQuarterTotalDays: number;
  previousQuarterDailyRate: number;
  targetDailyRate: number;
  points: PmoDelayTrendPoint[];
  summary: PmoDelaySummary;
}

/** 日数と件数 @graph-connects none */
export interface PmoDelayTally {
  days: number;
  count: number;
}

/** 原因内訳の小分類行 @graph-connects none */
export interface PmoDelayCauseChild {
  /** 小分類名（`正常系 - 要件定義` 等。分類なしは空文字） */
  subCause: string;
  total: PmoDelayTally;
  rework: PmoDelayTally;
  delay: PmoDelayTally;
}

/** 原因内訳の 1 行 @graph-connects none */
export interface PmoDelayCauseRow {
  cause: string;
  total: PmoDelayTally;
  rework: PmoDelayTally;
  delay: PmoDelayTally;
  /** 小分類の内訳（`仕様追加/変更` のみ。他は空配列） */
  children: PmoDelayCauseChild[];
}

/** 原因テーブルの絞り込み（合算 / 引き直しのみ / 遅延のみ） @graph-connects none */
export type PmoDelayCauseFilter = 'total' | 'rework' | 'delay';

/** @graph-connects none */
export interface PmoDelayTrendResponse {
  currentQuarter: PmoDelayQuarter;
  previousQuarter: PmoDelayQuarter;
  asOfDate: string;
  series: PmoDelaySeries[];
  causeBreakdown: PmoDelayCauseRow[];
  excludedRowCount: number;
}
